import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, LogOut, Building2, Check, X, User, CornerDownLeft } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { EsBadge } from "./EsBadge";

export const fmtGiorno = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  const s = d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const quando = (n) => (n < 0 ? `scaduta da ${-n} ${n === -1 ? "giorno" : "giorni"}` : n === 0 ? "oggi" : n === 1 ? "domani" : `tra ${n} giorni`);
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function useUsciteProgrammate(giorni = 7) {
  const { selectedStructure, user } = useAuth();
  return useQuery({
    queryKey: ["uscite-programmate", selectedStructure, giorni],
    enabled: !!user,
    refetchInterval: 60000,
    queryFn: async () => (await api.get("/movimenti/programmate", { params: { giorni, ...(selectedStructure && selectedStructure !== "all" ? { structure_id: selectedStructure } : {}) } })).data,
  });
}

const DataLabel = ({ value }) => value ? <div className="text-xs font-bold text-cyan-800 mt-1" data-testid="prog-data-label">{fmtGiorno(value)}</div> : null;

export function ProgrammaUscitaDialog({ open, onClose, ospiti = [], operatore, onSaved }) {
  const empty = { ospite_id: "", tipo: "uscita", data: ymd(new Date(Date.now() + 86400000)), ora: "10:00", accompagnatore: "", data_rientro_prevista: "", ora_rientro_prevista: "", motivo: "", note: "" };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(empty); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    if (!f.ospite_id || !f.data) { toast.error("Scegli l'ospite e la data"); return; }
    if (f.data_rientro_prevista && f.data_rientro_prevista < f.data) { toast.error("Il rientro previsto è prima dell'uscita"); return; }
    setSaving(true);
    try { await api.post("/movimenti/programma", { ...f, operatore }); toast.success("Uscita programmata"); onSaved(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setSaving(false);
  };
  const motivi = f.tipo === "ospedale" ? ["Accertamenti", "Visita specialistica", "Ricovero programmato", "Day hospital"] : ["Uscita con i parenti", "Visita medica", "Accertamenti", "Pranzo fuori", "Permesso"];
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="prog-uscita-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarClock size={18} className="text-cyan-700" /> Programma un'uscita</DialogTitle>
          <DialogDescription>Il giorno stabilito l'operatore conferma l'uscita con un tocco.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold mb-1 block">Ospite</label>
            <select value={f.ospite_id} onChange={set("ospite_id")} className="w-full h-10 rounded-md border bg-white px-3 text-sm" data-testid="prog-ospite">
              <option value="">— Scegli l'ospite —</option>
              {ospiti.map((o) => <option key={o.ospite_id} value={o.ospite_id}>{o.cognome} {o.nome}</option>)}
            </select>
          </div>
          <div className="flex rounded-lg overflow-hidden border w-full">
            {[["uscita", "Uscita", LogOut, "bg-amber-500"], ["ospedale", "Visita / ricovero", Building2, "bg-red-600"]].map(([v, l, I, c]) => (
              <button key={v} type="button" onClick={() => setF((p) => ({ ...p, tipo: v }))} className={`flex-1 h-10 text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${f.tipo === v ? `${c} text-white` : "bg-secondary hover:bg-secondary/70"}`} data-testid={`prog-tipo-${v}`}><I size={15} /> {l}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold mb-1 block">Giorno dell'uscita</label><Input type="date" value={f.data} min={ymd(new Date())} onChange={set("data")} data-testid="prog-data" /><DataLabel value={f.data} /></div>
            <div><label className="text-xs font-semibold mb-1 block">Ora prevista</label><Input type="time" value={f.ora} onChange={set("ora")} data-testid="prog-ora" /></div>
            <div><label className="text-xs font-semibold mb-1 block">Rientro previsto (giorno)</label><Input type="date" value={f.data_rientro_prevista} min={f.data} onChange={set("data_rientro_prevista")} data-testid="prog-rientro-data" /><DataLabel value={f.data_rientro_prevista} /></div>
            <div><label className="text-xs font-semibold mb-1 block">Rientro previsto (ora)</label><Input type="time" value={f.ora_rientro_prevista} onChange={set("ora_rientro_prevista")} data-testid="prog-rientro-ora" /></div>
          </div>
          <div><label className="text-xs font-semibold mb-1 block">Accompagnatore</label><Input value={f.accompagnatore} onChange={set("accompagnatore")} placeholder="Es. figlia Maria, ambulanza, ..." data-testid="prog-accompagnatore" /></div>
          <div>
            <label className="text-xs font-semibold mb-1 block">Motivo</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {motivi.map((m) => <button key={m} type="button" onClick={() => setF((p) => ({ ...p, motivo: m }))} className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${f.motivo === m ? "bg-cyan-700 text-white" : "bg-secondary hover:bg-secondary/70"}`} data-testid={`prog-motivo-${m}`}>{m}</button>)}
            </div>
            <Input value={f.motivo} onChange={set("motivo")} placeholder="Motivo dell'uscita" data-testid="prog-motivo" />
          </div>
          <div><label className="text-xs font-semibold mb-1 block">Note</label><Textarea rows={2} value={f.note} onChange={set("note")} placeholder="Es. portare documenti, digiuno dalla sera prima, ..." data-testid="prog-note" /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="prog-annulla">Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-cyan-700 hover:bg-cyan-800" data-testid="prog-salva">Programma uscita</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Riga = ({ u, onConferma, onAnnulla, compact }) => {
  const ric = u.tipo === "ospedale";
  const col = ric ? "text-red-700" : "text-amber-700";
  return (
    <div className={`rounded-xl border p-3 flex flex-col lg:flex-row lg:items-center gap-2 ${u.giorni_mancanti <= 0 ? "border-cyan-400 bg-cyan-50" : "bg-white"}`} data-testid={`prog-item-${u.id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-sm flex items-center flex-wrap gap-x-1.5">
          {ric ? <Building2 size={14} className="text-red-600" /> : <LogOut size={14} className="text-amber-600" />}
          {u.cognome} {u.nome}<EsBadge id={u.ospite_id} />
          <span className={`text-xs font-bold ${col}`}>· {u.tipo_label}{u.ora ? ` alle ${u.ora}` : ""}</span>
          {u.giorni_mancanti < 0 && <span className="text-[10px] font-bold rounded-full px-1.5 py-0.5 bg-red-100 text-red-700">{quando(u.giorni_mancanti)}</span>}
        </div>
        {!compact && <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
          {u.motivo && <span className="font-semibold text-foreground">{u.motivo}</span>}
          {u.accompagnatore && <span className="inline-flex items-center gap-0.5"><User size={11} /> {u.accompagnatore}</span>}
          {u.data_rientro_prevista && <span className="inline-flex items-center gap-0.5"><CornerDownLeft size={11} /> rientro {fmtGiorno(u.data_rientro_prevista)}{u.ora_rientro_prevista ? ` ore ${u.ora_rientro_prevista}` : ""}</span>}
        </div>}
        {!compact && u.note && <div className="text-[11px] text-muted-foreground italic mt-0.5">{u.note}</div>}
      </div>
      {(onConferma || onAnnulla) && (
        <div className="flex gap-1.5 shrink-0 w-full lg:w-auto">
          {onConferma && u.giorni_mancanti <= 0 && <Button size="sm" className="flex-1 lg:flex-none h-10 lg:h-9 bg-cyan-700 hover:bg-cyan-800" onClick={() => onConferma(u)} data-testid={`prog-conferma-${u.id}`}><Check size={15} className="mr-1" /> {u.tipo === "ospedale" ? "Conferma partenza" : "Conferma uscita"}</Button>}
          {onAnnulla && <Button size="sm" variant="outline" className="flex-1 lg:flex-none h-10 lg:h-9 text-red-700 border-red-200" onClick={() => onAnnulla(u)} data-testid={`prog-elimina-${u.id}`}><X size={15} className="mr-1" /> Annulla</Button>}
        </div>
      )}
    </div>
  );
};

export function UsciteProgrammateBox({ items = [], onConferma, onAnnulla, onNuova, testid = "prog-box", titolo = "Uscite programmate — prossimi giorni", compact = false }) {
  const giorni = {};
  items.forEach((u) => { const k = u.giorni_mancanti < 0 ? "scadute" : u.data; (giorni[k] = giorni[k] || []).push(u); });
  const keys = Object.keys(giorni).sort((a, b) => (a === "scadute" ? -1 : b === "scadute" ? 1 : a.localeCompare(b)));
  return (
    <div className="gr-card p-4 sm:p-5 border-l-4 border-cyan-600" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <CalendarClock size={18} className="text-cyan-700" />
        <h2 className="font-head font-bold">{titolo}</h2>
        <span className="text-xs font-bold rounded-full px-2.5 py-0.5 bg-cyan-700 text-white tabnum" data-testid={`${testid}-count`}>{items.length}</span>
        {onNuova && <Button size="sm" className="ml-auto bg-cyan-700 hover:bg-cyan-800" onClick={onNuova} data-testid={`${testid}-nuova`}><CalendarClock size={15} className="mr-1" /> Programma uscita</Button>}
      </div>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna uscita programmata nei prossimi giorni.</p> : (
        <div className="space-y-4">
          {keys.map((k) => (
            <div key={k}>
              <div className={`text-sm font-bold mb-1.5 ${k === "scadute" ? "text-red-700" : "text-cyan-800"}`}>
                {k === "scadute" ? "Da confermare (giorno già passato)" : `${fmtGiorno(k)} · ${quando(giorni[k][0].giorni_mancanti)}`}
              </div>
              <div className="space-y-2">{giorni[k].map((u) => <Riga key={u.id} u={u} onConferma={onConferma} onAnnulla={onAnnulla} compact={compact} />)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
